import type { ReactNode } from "react";
import { animeCategories, regions, statuses, years, sortOptions } from "@/mocks/anime";

interface FilterPanelProps {
  selectedGenres: string[];
  region: string;
  year: string;
  status: string;
  sort: string;
  onlyFav: boolean;
  onToggleGenre: (genre: string) => void;
  onClearGenres: () => void;
  onSetRegion: (value: string) => void;
  onSetYear: (value: string) => void;
  onSetStatus: (value: string) => void;
  onSetSort: (value: string) => void;
  onToggleFav: () => void;
  onReset: () => void;
}

const chipBase =
  "whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors";
const chipActive = "border-primary-500 bg-primary-500 text-background-50";
const chipIdle =
  "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700";

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/70 py-3.5 last:border-b-0 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-xs font-medium text-foreground-500 sm:w-16">{label}</span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export default function FilterPanel({
  selectedGenres,
  region,
  year,
  status,
  sort,
  onlyFav,
  onToggleGenre,
  onClearGenres,
  onSetRegion,
  onSetYear,
  onSetStatus,
  onSetSort,
  onToggleFav,
  onReset,
}: FilterPanelProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-1 md:px-5">
      <FilterRow label="类型">
        <button
          type="button"
          onClick={onClearGenres}
          className={`${chipBase} ${selectedGenres.length === 0 ? chipActive : chipIdle}`}
        >
          全部
        </button>
        {animeCategories.map((category) => (
          <button
            key={category.name}
            type="button"
            onClick={() => onToggleGenre(category.name)}
            className={`${chipBase} ${selectedGenres.includes(category.name) ? chipActive : chipIdle}`}
          >
            {category.name}
          </button>
        ))}
      </FilterRow>

      <FilterRow label="地区">
        {regions.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSetRegion(item)}
            className={`${chipBase} ${region === item ? chipActive : chipIdle}`}
          >
            {item}
          </button>
        ))}
      </FilterRow>

      <FilterRow label="年份">
        {years.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSetYear(item)}
            className={`${chipBase} ${year === item ? chipActive : chipIdle}`}
          >
            {item}
          </button>
        ))}
      </FilterRow>

      <FilterRow label="状态">
        {statuses.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSetStatus(item)}
            className={`${chipBase} ${status === item ? chipActive : chipIdle}`}
          >
            {item}
          </button>
        ))}
      </FilterRow>

      <FilterRow label="排序">
        {sortOptions.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onSetSort(item)}
            className={`${chipBase} ${sort === item ? chipActive : chipIdle}`}
          >
            {item}
          </button>
        ))}
      </FilterRow>

      <div className="flex flex-col gap-3 py-3.5 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onToggleFav}
          className={[
            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors",
            onlyFav
              ? "border-primary-500 bg-primary-100 text-primary-700"
              : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700",
          ].join(" ")}
        >
          <i className={onlyFav ? "ri-heart-3-fill" : "ri-heart-3-line"} />
          只看我的收藏
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
        >
          <i className="ri-refresh-line" />
          重置全部筛选
        </button>
      </div>
    </div>
  );
}